import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock, FileSignature, FileText, PenLine } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, errMsg, fmtDay, fmtDateTime } from "../../lib/api";
import { invalidatePolicyAccess } from "../../lib/policies";
import { PolicyPdfViewer } from "../../components/policies/PolicyPdfViewer";
import { PolicySignFlow } from "../../components/policies/PolicySignGate";

/**
 * Phase 218 — "My Policies": read-only list of the policies this staff member is selected for, with the PDF and the
 * date / time of their signature. Pending ones can be signed right here too (same flow as the login screen).
 * Used by every staff portal (admin, supervisor, collector, marshal) — `compact` for the mobile shells.
 */
export default function MyPolicies({ compact = false }) {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const [signing, setSigning] = useState(null);
  const load = useCallback(() => api.get(`/t/${code}/policies/mine`).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ items: [], total: 0, pending: 0 }); }), [code]);
  useEffect(() => { load(); }, [load]);

  const header = compact ? (
    <div className="mb-3">
      <h1 className="text-lg font-semibold font-heading flex items-center gap-2" data-testid="my-policies-title"><FileSignature className="h-5 w-5" /> My Policies</h1>
      <p className="text-xs text-muted-foreground">Company policies you must read and sign</p>
    </div>
  ) : (
    <PageHeader title="My Policies" subtitle="Company policies assigned to you — open the document any time and see when you signed it">
      {data && data.pending > 0 && <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100" data-testid="my-policies-pending-badge">{data.pending} pending</Badge>}
    </PageHeader>
  );

  return (
    <div data-testid="my-policies-page">
      {header}
      {!data ? <Spinner /> : data.items.length === 0 ? (
        <Card className="shadow-sm"><CardContent className="p-0"><EmptyState title="No policies assigned to you" subtitle="When a policy is assigned to you, it appears here and you are asked to sign it at your next login." /></CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="my-policies-list">
          {data.items.map((p) => (
            <Card key={p.id} className={`shadow-sm ${p.signed ? "" : "border-amber-300"}`} data-testid={`my-policy-${p.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium leading-snug" data-testid={`my-policy-title-${p.id}`}>{p.title}</p>
                    {p.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-3">{p.description}</p>}
                  </div>
                  <Badge variant="outline" className="font-mono shrink-0">v{p.version}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Effective {fmtDay(p.effective_date)}</span>
                  <span className="flex items-center gap-1 font-mono"><FileText className="h-3.5 w-3.5" /> {p.filename}</span>
                </div>
                {p.signed ? (
                  <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid={`my-policy-signed-${p.id}`}>
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Signed on <b>{fmtDateTime(p.signature?.signed_at)}</b> (IST) as <i className="font-heading">{p.signature?.typed_name}</i>{p.earlier_signatures ? ` • ${p.earlier_signatures} earlier version${p.earlier_signatures === 1 ? "" : "s"} also signed` : ""}</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`my-policy-pending-${p.id}`}>
                    <Clock className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Signature pending — {p.revision_reason === "new_version" ? "a new version was uploaded" : p.revision_reason === "edited" ? "the policy details were updated" : "you have been asked to sign this policy"}. You will be asked at your next login, or sign it now.</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setView(p)} data-testid={`my-policy-open-${p.id}`}><FileText className="h-4 w-4 mr-1.5" /> Open document</Button>
                  {!p.signed && <Button size="sm" onClick={() => setSigning(p)} data-testid={`my-policy-sign-${p.id}`}><PenLine className="h-4 w-4 mr-1.5" /> Read and sign now</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {view && (
        <Dialog open onOpenChange={(o) => !o && setView(null)}>
          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="my-policy-view-dialog">
            <DialogHeader><DialogTitle className="font-heading">{view.title} <Badge variant="outline" className="font-mono ml-1">v{view.version}</Badge></DialogTitle><DialogDescription>Effective {fmtDay(view.effective_date)}{view.description ? ` — ${view.description}` : ""}</DialogDescription></DialogHeader>
            <PolicyPdfViewer code={code} policy={view} height="h-[65vh] min-h-[320px]" testId="my-policy-pdf" />
          </DialogContent>
        </Dialog>
      )}
      {signing && (
        <Dialog open onOpenChange={(o) => !o && setSigning(null)}>
          <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none" data-testid="my-policy-sign-dialog">
            <DialogHeader className="sr-only"><DialogTitle>Sign {signing.title}</DialogTitle><DialogDescription>Read and sign the policy</DialogDescription></DialogHeader>
            <PolicySignFlow code={code} policies={[signing]} embedded onDone={() => { setSigning(null); invalidatePolicyAccess(); load(); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
